import { Injectable } from '@angular/core';
import {
	DEFAULT_VIDEO_OBJECT_POSITION,
	getCoverRegionCenterNormalized,
	type VideoObjectPosition,
} from './video-cover-frame.util';

export interface INormalizedFocusPoint {
	x: number;
	y: number;
}

type FocusModeCapability = 'none' | 'manual' | 'single-shot' | 'continuous';

@Injectable({
	providedIn: 'root',
})
export class CameraFocusService {
	private readonly detectionRefocusThrottleMs = 2000;
	private readonly tapRefocusThrottleMs = 500;
	private lastDetectionRefocusAtMs = 0;
	private lastTapRefocusAtMs = 0;

	public async applyContinuousFocus(track: MediaStreamTrack | null | undefined): Promise<boolean> {
		if (!track || track.readyState === 'ended') return false;

		const modes = this.getFocusModes(track);
		if (!modes.includes('continuous')) {
			return false;
		}

		try {
			await track.applyConstraints({
				advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet],
			});
			return true;
		} catch {
			return false;
		}
	}

	/** Autofocus continuo + POI en el centro del cover visible sobre el sensor. */
	public async applyInitialFocus(
		track: MediaStreamTrack | null | undefined,
		videoWidth: number,
		videoHeight: number,
		viewportAspect: number,
		objectPosition: VideoObjectPosition = DEFAULT_VIDEO_OBJECT_POSITION,
	): Promise<boolean> {
		await this.applyContinuousFocus(track);
		const center = getCoverRegionCenterNormalized(videoWidth, videoHeight, viewportAspect, objectPosition);
		const poiOk = await this.refocusAtNormalizedPoint(track, center);
		return poiOk;
	}

	public async refocusAtNormalizedPoint(
		track: MediaStreamTrack | null | undefined,
		point: INormalizedFocusPoint,
	): Promise<boolean> {
		if (!track || track.readyState === 'ended') return false;

		const focusPoint = { x: this.clamp01(point.x), y: this.clamp01(point.y) };
		const modes = this.getFocusModes(track);
		const caps = track.getCapabilities?.() as MediaTrackCapabilities & {
			pointsOfInterest?: Array<{ x: number; y: number }>;
		};
		const hasPointsOfInterest = Boolean(caps && 'pointsOfInterest' in caps);

		try {
			if (hasPointsOfInterest) {
				await track.applyConstraints({
					advanced: [{ pointsOfInterest: [focusPoint] } as MediaTrackConstraintSet],
				});
			}

			if (modes.includes('single-shot')) {
				await track.applyConstraints({
					advanced: [{ focusMode: 'single-shot' } as MediaTrackConstraintSet],
				});
			}

			if (modes.includes('continuous')) {
				await track.applyConstraints({
					advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet],
				});
			}

			if (!hasPointsOfInterest && !modes.includes('single-shot')) {
				return false;
			}

			return true;
		} catch {
			return false;
		}
	}

	/** Evita trabajo de constraints si el throttle aún no ha expirado (NIF-385). */
	public isDetectionRefocusThrottled(): boolean {
		return Date.now() - this.lastDetectionRefocusAtMs < this.detectionRefocusThrottleMs;
	}

	public async triggerDetectionRefocus(
		track: MediaStreamTrack | null | undefined,
		point: INormalizedFocusPoint,
	): Promise<boolean> {
		const now = Date.now();
		if (now - this.lastDetectionRefocusAtMs < this.detectionRefocusThrottleMs) {
			return false;
		}
		// Throttle por intento (como tapRefocus): si solo actualizamos en éxito y el dispositivo
		// no soporta POI/focus, se reintenta cada frame y puede desestabilizar la detección.
		this.lastDetectionRefocusAtMs = now;
		return this.refocusAtNormalizedPoint(track, point);
	}

	/** Refocus por tap del usuario; mínimo 500 ms entre invocaciones. */
	public async triggerTapRefocus(
		track: MediaStreamTrack | null | undefined,
		point: INormalizedFocusPoint,
	): Promise<boolean> {
		const now = Date.now();
		if (now - this.lastTapRefocusAtMs < this.tapRefocusThrottleMs) {
			return false;
		}
		this.lastTapRefocusAtMs = now;
		return this.refocusAtNormalizedPoint(track, point);
	}

	public supportsTapToFocus(track: MediaStreamTrack | null | undefined): boolean {
		if (!track) return false;
		const modes = this.getFocusModes(track);
		const caps = track.getCapabilities?.() as MediaTrackCapabilities & { pointsOfInterest?: unknown };
		return modes.includes('single-shot') || Boolean(caps && 'pointsOfInterest' in caps);
	}

	private getFocusModes(track: MediaStreamTrack): FocusModeCapability[] {
		const caps = track.getCapabilities?.() as { focusMode?: FocusModeCapability[] } | undefined;
		return caps?.focusMode ?? [];
	}

	private clamp01(value: number): number {
		return Math.min(1, Math.max(0, value));
	}
}
